import type { VoteOption, VotingSessionData } from "@afas-doe-maar/models";
import { useCallback, useEffect, useMemo, useState } from "react";
import { VotingSessionContext } from "./VotingSessionContext";
import { useStore } from "~/hooks/useStore";
import { useEventStore } from "~/providers/eventStore/EventStoreContext";

export type VotingSessionContextValue = ReturnType<typeof useContextValue>;

function useContextValue() {
  const { eventStore } = useEventStore();
  const session = useStore<VotingSessionData>(eventStore.votingSession, ["store/votingSession"]);

  // Get voter when signed in
  const [voterId, setVoterId] = useState<string | null>(null);
  const voterStore = useMemo(
    () => (voterId ? eventStore.voter(voterId) : null),
    [eventStore, voterId],
  );
  const voter = useStore(voterStore, ["store/voter"]);

  // Automatically sign in when there is no voter
  useEffect(() => {
    if (voterId) return;

    let mounted = true;

    (async () => {
      const result = await eventStore.allVoters.signIn();

      if (!mounted) return;
      if (!result.success) {
        throw new Error(`Failed to sign in: ${result.code}`);
      }

      setVoterId(result.userId);
    })();

    return () => {
      mounted = false;
    };
  }, [eventStore.allVoters, voterId]);

  // Track presence of signed in voter
  useEffect(() => {
    voterStore?.trackPresence();
  }, [voterStore]);

  // Methods
  const makeVote = useCallback(
    (vote: VoteOption | null) => {
      voterStore?.update({ vote });
    },
    [voterStore],
  );

  return useMemo(
    () => ({
      session: session ?? { state: "waiting" },
      voter,
      makeVote,
    }),
    [session, voter, makeVote],
  );
}

export function VotingSessionProvider({ children }: { children: React.ReactNode }) {
  const contextValue = useContextValue();
  return (
    <VotingSessionContext.Provider value={contextValue}>{children}</VotingSessionContext.Provider>
  );
}
